import { type JSX, useEffect, useMemo, useRef, useState } from "react";

import { type Meta } from "@vega-api/generated/types/meta";
import { ThemedDropdown } from "@vega-core/components/Form/Controls/Dropdown/ThemedDropdown/ThemedDropdown";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationItemsInfo,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationWrapper,
} from "@vega-core/components/shadcn/pagination";
import { useDeviceWidth } from "@vega-core/hooks/useDeviceWidth";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "@vega-core/hooks/usePaginationState";
import { type OptionType } from "@vega-core/models/options";
import { m } from "@vega-core/paraglide/messages";
import { isFalsy } from "@vega-core/utils/isFalsy";
import { isTruthy } from "@vega-core/utils/isTruthy";

type UsePaginationProps = {
  meta?: Meta | null;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  isLoading: boolean;
  currentPageSize: number;
  setCurrentPageSize: (pageSize: number) => void;
  filterDependencies?: unknown[];
  pageSizeOptions?: number[];
};

export const usePagination = ({
  meta,
  currentPage,
  setCurrentPage,
  isLoading,
  currentPageSize,
  setCurrentPageSize,
  filterDependencies = [],
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}: UsePaginationProps) => {
  const [memoizedMeta, setMemoizedMeta] = useState(meta);
  const isFirstRender = useRef(true);
  const isMobile = !useDeviceWidth("sm");

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      return;
    }

    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [...filterDependencies]);

  useMemo(() => {
    if (meta !== memoizedMeta && isTruthy(meta)) {
      setMemoizedMeta(meta);
    }
  }, [meta, memoizedMeta]);

  const hasNextPage = isTruthy(memoizedMeta?.hasNextPage);
  const totalPages = memoizedMeta?.totalPages ?? 0;
  const pageSize = memoizedMeta?.pageSize ?? currentPageSize;

  const handlePageChange = (newPage: number) => {
    if (newPage !== currentPage) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    if (newPageSize !== currentPageSize) {
      setCurrentPageSize(newPageSize);
      setCurrentPage(1);
    }
  };

  const GetPageSizeSelector = () => {
    if (isMobile) {
      return null;
    }

    const minPageSize = Math.min(...DEFAULT_PAGE_SIZE_OPTIONS);
    const totalItems = memoizedMeta?.count ?? 0;

    // If there are less items than the minimum page size, don't show the page size selector
    if (totalItems <= minPageSize) {
      return null;
    }

    const options = pageSizeOptions.map((size) => ({
      label: size,
      value: size.toString(),
    }));

    return (
      <div className="flex items-center gap-2 shrink-0">
        <div className="body-base-medium text-text/primary whitespace-nowrap">
          <span className="hidden md:inline">{m.common_itemsPerPage()}</span>
          <span className="inline md:hidden">{m.common_items()}</span>
        </div>
        <ThemedDropdown
          buttonProps={{ className: "h-10 min-h-10 min-w-16" }}
          isClearable={false}
          isLoading={isLoading}
          name="page-size-selector"
          onChange={(option) => {
            if (option) {
              handlePageSizeChange(parseInt(option.value, 10));
            }
          }}
          options={options as unknown as OptionType[]}
          value={pageSize.toString()}
        />
      </div>
    );
  };

  const GetPaginationItems = () => {
    if (totalPages <= 1) {
      return [];
    }
    const items: JSX.Element[] = [];

    // Previous Page Button
    items.push(
      <PaginationItem key="prev">
        <PaginationPrevious
          disabled={currentPage === 1}
          href="#"
          isLoading={isLoading}
          onClick={(e) => {
            e.preventDefault();

            if (currentPage > 1) {
              handlePageChange(currentPage - 1);
            }
          }}
        />
      </PaginationItem>,
    );

    // First Page
    if (currentPage > 2) {
      items.push(
        <PaginationItem key={1}>
          <PaginationLink
            href="#"
            isLoading={isLoading}
            onClick={(e) => {
              e.preventDefault();
              handlePageChange(1);
            }}
          >
            1
          </PaginationLink>
        </PaginationItem>,
      );
    }

    // Ellipsis Before Current Page
    if (currentPage > 3) {
      items.push(<PaginationEllipsis key="ellipsis-start" />);
    }

    // Page Numbers Around Current Page
    const startPage = Math.max(1, currentPage - 1);
    const endPage = Math.min(totalPages, currentPage + 1);

    for (let pageNum = startPage; pageNum <= endPage; pageNum++) {
      items.push(
        <PaginationItem key={pageNum}>
          <PaginationLink
            href="#"
            isActive={pageNum === currentPage}
            isLoading={isLoading}
            onClick={(e) => {
              e.preventDefault();
              handlePageChange(pageNum);
            }}
          >
            {pageNum}
          </PaginationLink>
        </PaginationItem>,
      );
    }

    // Ellipsis After Current Page
    if (currentPage < totalPages - 2) {
      items.push(<PaginationEllipsis key="ellipsis-end" />);
    }

    // Last Page
    if (currentPage < totalPages - 1) {
      items.push(
        <PaginationItem key={totalPages}>
          <PaginationLink
            href="#"
            isLoading={isLoading}
            onClick={(e) => {
              e.preventDefault();
              handlePageChange(totalPages);
            }}
          >
            {totalPages}
          </PaginationLink>
        </PaginationItem>,
      );
    }

    // Next Page Button
    items.push(
      <PaginationItem key="next">
        <PaginationNext
          disabled={totalPages === currentPage}
          href="#"
          isLoading={isLoading}
          onClick={(e) => {
            e.preventDefault();

            if (isTruthy(hasNextPage)) {
              handlePageChange(currentPage + 1);
            }
          }}
        />
      </PaginationItem>,
    );

    return items;
  };

  const GetPaginationItemsInfo = () => {
    if (isFalsy(meta) || isFalsy(meta.page) || isFalsy(meta.count)) {
      return null;
    }

    const currentPage = meta.page;
    const pageSize = meta.pageSize ?? currentPageSize;
    const totalCount = meta.count;

    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalCount);

    return (
      <span className="whitespace-nowrap shrink-0 mr-2">
        {`${startItem}-${endItem} ${m.common_of().toLowerCase()} ${totalCount} ${m.common_items()}`}
      </span>
    );
  };

  const paginationItemsInfo =
    isTruthy(meta?.count) && meta.count > 0 ? (
      <PaginationItemsInfo className="shrink-0" isLoading={isLoading}>
        <GetPageSizeSelector />
        <GetPaginationItemsInfo />
      </PaginationItemsInfo>
    ) : null;

  const paginationElement =
    totalPages > 1 ? (
      <PaginationWrapper>
        {paginationItemsInfo}
        <Pagination className="sm:ml-auto">
          <PaginationContent className="overflow-hidden">
            <GetPaginationItems />
          </PaginationContent>
        </Pagination>
      </PaginationWrapper>
    ) : (
      paginationItemsInfo
    );

  return { currentPage, paginationElement, setCurrentPage };
};
